import { useCallback, useState } from 'react';
import type { MutableRefObject } from 'react';
import { flushSync } from 'react-dom';
import type Konva from 'konva';

export const MIN_ORG_CARD_DIMENSION = 20;

export const resolveOrgCardDimension = (value: unknown, fallback: number): number => {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;
};

export const resolveOrgCardHeightWithFooter = (
  persistedHeight: number,
  defaultBodyHeight: number,
  footerHeight: number,
): number => {
  if (footerHeight <= 0) return persistedHeight;

  const isBodyOnlyDefaultHeight = Math.abs(persistedHeight - defaultBodyHeight) <= 1;
  return isBodyOnlyDefaultHeight ? persistedHeight + footerHeight : persistedHeight;
};

let textMeasureContext: CanvasRenderingContext2D | null | undefined;

const resolveTextMeasureContext = (): CanvasRenderingContext2D | null => {
  if (textMeasureContext !== undefined) return textMeasureContext;
  if (typeof document === 'undefined') {
    textMeasureContext = null;
    return textMeasureContext;
  }

  textMeasureContext = document.createElement('canvas').getContext('2d');
  return textMeasureContext;
};

const measureTextWidth = (
  text: string,
  fontSize: number,
  fontFamily: string,
  fontStyle?: string,
): number => {
  const context = resolveTextMeasureContext();
  if (!context) return text.length * fontSize * 0.58;

  const fontPrefix = fontStyle && fontStyle !== 'normal' ? `${fontStyle} ` : '';
  context.font = `${fontPrefix}${fontSize}px ${fontFamily}`;
  return context.measureText(text).width;
};

export function resolveOrgCardTextLineCount(input: {
  text: string;
  textWidth: number;
  fontSize: number;
  fontFamily: string;
  fontStyle?: string;
  maxLines?: number;
}): number {
  const { text, textWidth, fontSize, fontFamily, fontStyle, maxLines = 2 } = input;
  const normalized = text.trim();
  const width = Number.isFinite(textWidth) ? textWidth : 0;
  if (!normalized || width <= 0 || fontSize <= 0 || maxLines <= 1) return 1;

  if (measureTextWidth(normalized, fontSize, fontFamily, fontStyle) <= width) {
    return 1;
  }

  let currentLine = '';
  const words = normalized.split(/\s+/);
  let lineCount = 1;

  for (const word of words) {
    const candidate = currentLine ? `${currentLine} ${word}` : word;
    if (measureTextWidth(candidate, fontSize, fontFamily, fontStyle) <= width) {
      currentLine = candidate;
      continue;
    }

    if (currentLine) {
      lineCount += 1;
      currentLine = word;
    } else {
      lineCount += 1;
      currentLine = '';
    }

    if (lineCount >= maxLines) return maxLines;
  }

  return Math.max(1, Math.min(maxLines, lineCount));
}

export function useOrgCardLiveResize(input: {
  groupRef: MutableRefObject<Konva.Group | null>;
  width: number;
  height: number;
  onTransform?: (e: Konva.KonvaEventObject<Event>) => void;
  onTransformEnd?: (e: Konva.KonvaEventObject<Event>) => void;
}) {
  const { groupRef, width, height, onTransform, onTransformEnd } = input;
  const [liveSize, setLiveSize] = useState<{ w: number; h: number } | null>(null);

  const handleTransform = useCallback((e: Konva.KonvaEventObject<Event>) => {
    const target = e.target as Konva.Group;
    const scaleX = target?.scaleX?.() ?? 1;
    const scaleY = target?.scaleY?.() ?? 1;
    const currentWidth = groupRef.current?.width?.() || width;
    const currentHeight = groupRef.current?.height?.() || height;
    const nextWidth = Math.max(MIN_ORG_CARD_DIMENSION, currentWidth * scaleX);
    const nextHeight = Math.max(MIN_ORG_CARD_DIMENSION, currentHeight * scaleY);

    try {
      groupRef.current?.width(nextWidth);
      groupRef.current?.height(nextHeight);
      target?.width?.(nextWidth);
      target?.height?.(nextHeight);
      target?.scaleX?.(1);
      target?.scaleY?.(1);
    } catch {}

    flushSync(() => setLiveSize({ w: nextWidth, h: nextHeight }));
    try { target?.getLayer?.()?.batchDraw?.(); } catch {}
    onTransform?.(e);
  }, [groupRef, height, onTransform, width]);

  const handleTransformEnd = useCallback((e: Konva.KonvaEventObject<Event>) => {
    onTransformEnd?.(e);
    setTimeout(() => setLiveSize(null), 0);
  }, [onTransformEnd]);

  return { liveSize, handleTransform, handleTransformEnd };
}
